import { FadeUp, Eyebrow, LineReveal } from "@/components/Motion";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const TEAM = [
  {
    name: "Haad Bakshi",
    role: "CEO & Producer",
    bio: "Founder of Incolour Films — director, producer, and cinematographer across the studio's films, commercials, and music videos.",
  },
  {
    name: "Zehra Abrar",
    role: "Executive Producer & Lawyer",
    bio: "Guides productions from financing through contracts — keeping the studio's deals as clean as its frames.",
  },
  {
    name: "Lee Nisar",
    role: "Producer & Director",
    bio: "Produces and directs across the narrative slate, from first treatment to final cut.",
  },
  {
    name: "Moiz Mufti",
    role: "Business Development & Accounts",
    bio: "First call for new productions — and the steady hand on budgets and accounts that keeps every shoot moving.",
  },
  {
    name: "Piera Savage",
    role: "Entertainment Lawyer & Tax Credits",
    bio: "Production legal, chain of title, and the tax-credit strategy that makes shooting in Canada pay.",
  },
  {
    name: "Aivry Napier",
    role: "Production Designer",
    bio: "Builds the worlds — sets, props, and the details that make a frame believable.",
  },
];

const PARTNERS = [
  "ISNA Canada",
  "Maple Acura",
  "Bellwoods Coffee & Gelato",
  "Inhouse",
  "Harman",
  "Notifi",
  "Promethea",
  "Lie Down Films",
  "SOTO",
];



export default function About() {
  return (
    <div className="pt-16" data-testid="about-page">
      <SEO
        title="About Our Toronto Film Production Company"
        description="Incolour Films is a Toronto production company bringing filmmakers from diverse backgrounds together to develop original films and providing full-service production across Ontario."
        path="/about"
      />

      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Eyebrow>About Incolour</Eyebrow>

        <h1
          className="mt-5 font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl"
          data-testid="about-title"
        >
          <LineReveal
            lines={[
              "A Toronto production company",
              "built like a community.",
            ]}
          />
        </h1>

        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Incolour Films is a Toronto production company developing original films
            and providing full-service production across Ontario. Founded by Haad
            Bakshi, the company grew from his work in commercials, music videos
            and independent film into a community of filmmakers, producers and
            creative collaborators.
          </p>
        </FadeUp>
      </section>

      {/* MANIFESTO */}
      <section
        className="border-y border-border bg-cream2 py-16 dark:bg-card lg:py-24"
        data-testid="about-manifesto"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>What Think Incolour Means</Eyebrow>

          <div className="mt-5 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Different voices belong in the room — and on the screen.
            </h2>

            <div>
              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                Think Incolour means treating culture, perspective and lived
                experience as creative strengths. We bring filmmakers with distinct
                points of view together and help turn their stories into productions.
              </p>

              <Link
                to="/contact"
                className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold"
                data-testid="about-bring-story-link"
              >
                Bring us your story
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* TEAM */}
      <section
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
        data-testid="team-section"
      >
        <Eyebrow>The People</Eyebrow>

        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Small team, deep bench.
        </h2>

        <div className="mt-8 grid gap-x-10 sm:grid-cols-2">
          {TEAM.map((t) => (
            <div key={t.name} className="border-t border-border py-6" data-testid={`team-member-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <h3 className="font-display text-xl font-bold tracking-tight">{t.name}</h3>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.12em] text-orange">{t.role}</p>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">{t.bio}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COMMUNITY */}
      <section
        className="bg-ink py-16 text-cream lg:py-24"
        data-testid="community-section"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-cream/50">
            <span className="inline-block h-2 w-2 bg-orange" /> Clients &amp; Collaborators
          </p>

          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Built through relationships, project by project.
          </h2>

          <div className="mt-10 flex flex-wrap gap-3">
            {PARTNERS.map((p) => (
              <span
                key={p}
                className="rounded-full border border-cream/25 px-5 py-2.5 text-sm text-cream/85 transition-colors hover:border-orange hover:text-orange"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-testid="about-pathways">
        <Eyebrow>Work With Incolour</Eyebrow>
        <div className="mt-4 flex flex-col items-start justify-between gap-7 lg:flex-row lg:items-center">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Bring us your next project.</h2>
            <div className="mt-5 flex flex-wrap gap-6 text-sm font-semibold">
              <Link to="/original" data-testid="about-originals-link" className="inline-flex items-center gap-2 hover:text-orange">Explore Original Work <ArrowUpRight size={16} /></Link>
              <Link to="/service" data-testid="about-service-link" className="inline-flex items-center gap-2 hover:text-orange">Explore Service Production <ArrowUpRight size={16} /></Link>
            </div>
          </div>
          <Link to="/contact" data-testid="about-cta-link" className="inline-flex items-center gap-3 rounded-full bg-cream px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-orange">Start a conversation <ArrowUpRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
